import React from "react";
import { Send, Inbox, ShoppingBag, Calendar, Phone, Home } from "react-feather";
import toast from "react-hot-toast";
import SectionCard from "../../../components/common/SectionCard";
import CompactInfoBlock, { CompactInfoField } from "../../../components/common/CompactInfoBlock";
import { useGetTechnicalPendingAssignmentQuery, useAssignToTechnicalMutation } from "../technicalApi";
import { formatElapsedSince } from "../workElapsed";
import PendingWorkCostBox from "../components/PendingWorkCostBox";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { MAROON } from "../../../components/common/brandColors";

const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;

// Elapsed comes from the shared helper so this queue ages a row exactly as the
// work list and the Flutter screens do - "1d 4hrs", not a calendar-day count
// that reads "today" on something logged 23 hours ago.

// The "ready to push" queue (Technical.AssignToTechnical).
//
// When a Sales Order is confirmed and its service mix includes anything other
// than Guards, a Technical Work row is created at ON_HOLD / "NEW ORDER" with
// no PushedToTechnicalDate - deliberately invisible to the Technical Manager
// until someone in admin/ops pushes it. That is the whole reason this queue
// exists as a separate screen: sp_app_technical_work_list filters on
// PushedToTechnicalDate IS NOT NULL, so these rows appear nowhere else.
//
// Complaints and Service Visits never appear here - they are pushed in the
// same statement that creates them, because they go straight to the manager
// with no approval step.
const PendingAssignmentPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const { data, isLoading, isError, error } = useGetTechnicalPendingAssignmentQuery({
    branchId: selectedBranchId ?? undefined,
    pageSize: 200,
  });
  const [assignToTechnical, { isLoading: isPushing }] = useAssignToTechnicalMutation();

  const items = data?.items || [];

  const handlePush = async (row) => {
    try {
      await assignToTechnical(row.recordGuid).unwrap();
      toast.success(`${row.workNumber} pushed to Technical`);
      // Stay on the queue (2026-10-09: "once I push one it goes to the tech work page") - the
      // pushed row drops off by itself (the mutation invalidates this list), so the next one
      // can be pushed straight away.
    } catch (e) {
      toast.error(e?.message || "Could not push this work item");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Pending Assignment</h4>
            <h6 className="text-muted">
              Confirmed orders with non-Guards work, waiting to be handed to the Technical Manager
            </h6>
          </div>
        </div>

        {isLoading && <p>Loading...</p>}

        {isError && (
          <div className="alert alert-danger">
            Could not load the queue ({error?.message || "server error"}).
          </div>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <SectionCard icon={Inbox} title="Nothing waiting">
            <p className="text-muted mb-0">
              Every confirmed order with technical work has already been pushed. New ones appear here automatically when
              a sales order that includes Alarms or Other work is confirmed.
            </p>
          </SectionCard>
        )}

        <div className="row">
          {items.map((row) => {
            const waiting = formatElapsedSince(row.createdDate);
            return (
              <div className="col-xl-4 col-md-6" key={row.recordGuid}>
                <SectionCard
                  icon={ShoppingBag}
                  // Customer is the card heading; the TW- number moves into
                  // the body (2026-09-12, same rule as every list).
                  title={row.customerName || row.workNumber}
                  right={
                    <span
                      className="badge"
                      style={
                        waiting?.isLong
                          ? { background: "#FDECEA", color: "#C62828", fontWeight: 700 }
                          : { background: `${MAROON}14`, color: MAROON, fontWeight: 700 }
                      }
                      title="Waiting since it was created"
                    >
                      {waiting ? `${waiting.label} waiting` : "—"}
                    </span>
                  }
                >
                  <div className="text-muted mb-1" style={{ fontSize: 12.5 }}>{row.workNumber}</div>
                  <CompactInfoBlock className="mb-2">
                    <CompactInfoField label="Phone" value={row.customerPhone} icon={Phone} />
                    <CompactInfoField label="Branch" value={row.branchCode} icon={Home} />
                    <CompactInfoField label="Order" value={row.orderNumber} icon={ShoppingBag} />
                    <CompactInfoField label="Created" value={formatDay(row.createdDate)} icon={Calendar} />
                  </CompactInfoBlock>
                  <PendingWorkCostBox salesOrderRecordGuid={row.salesOrderRecordGuid} />
                  {/* "NEW ORDER" is the marker every row in this queue is created
                      with - true of all of them, so it says nothing. A remark
                      someone actually typed still shows. */}
                  {row.onHoldRemarks && row.onHoldRemarks.trim().toUpperCase() !== "NEW ORDER" && (
                    <div className="text-muted mb-2" style={{ fontSize: 12.5 }}>{row.onHoldRemarks}</div>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm w-100"
                    disabled={isPushing}
                    onClick={() => handlePush(row)}
                  >
                    <Send size={14} className="me-1" /> Push to Technical
                  </button>
                </SectionCard>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PendingAssignmentPage;
